/**
 * Column Name Utilities
 * 
 * Utilities for cleaning and formatting column names in results
 */

/**
 * Removes hash suffix from structural column names
 * 
 * Examples:
 * - session_e151143732966f3a -> session
 * - interval_start_abc123 -> interval_start
 * - date_xyz789 -> date
 * 
 * @param columnName - Column name that may contain a hash suffix
 * @returns Cleaned column name without hash
 */
export function cleanColumnName(columnName: string): string {
  // Pattern: column_name_hash where hash is hexadecimal (typically 16 chars)
  // Match: column_name_ followed by hexadecimal characters
  const hashPattern = /^(.+)_[a-f0-9]{8,}$/i;
  const match = columnName.match(hashPattern);
  
  if (match) {
    // Check if the base name is a known structural column
    const baseName = match[1];
    const structuralColumns = ['session', 'interval_start', 'interval_number', 'interval_id', 'date', 'interval_index'];
    
    if (structuralColumns.includes(baseName)) {
      return baseName;
    }
  }
  
  // Return original if no hash pattern matches or not a structural column
  return columnName;
}

/**
 * Cleans column names in a data object or array
 * 
 * @param data - Data object or array of objects
 * @returns Data with cleaned column names
 */
export function cleanColumnNames<T extends Record<string, any>>(data: T): T;
export function cleanColumnNames<T extends Record<string, any>>(data: T[]): T[];
export function cleanColumnNames<T extends Record<string, any>>(data: T | T[]): T | T[] {
  if (Array.isArray(data)) {
    return data.map((row) => {
      const cleaned: Record<string, any> = {};
      for (const [key, value] of Object.entries(row)) {
        cleaned[cleanColumnName(key)] = value;
      }
      return cleaned as T;
    });
  } else {
    const cleaned: Record<string, any> = {};
    for (const [key, value] of Object.entries(data)) {
      cleaned[cleanColumnName(key)] = value;
    }
    return cleaned as T;
  }
}
